import {
  createContext,
  useCallback,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useFlickerGuard } from '../../hooks/useFlickerGuard';

interface LoadingContextValue {
  register: (id: string) => void;
  unregister: (id: string) => void;
}

const LoadingContext = createContext<LoadingContextValue | null>(null);

export interface LoadingSkeletonProviderProps {
  children: ReactNode;
  /**
   * The app's skeleton element (e.g. `<HelpdeskLoadingSkeleton fullScreen />`,
   * `<PlaybookLoadingSkeleton fullScreen />`).
   */
  skeleton: ReactNode;
  /** Min visible window once skeleton appears (ms). Defaults to the hook default. */
  minVisibleMs?: number;
  /** Delayed-show window after first source registers (ms). */
  delayShowMs?: number;
  /**
   * Minimum delay after `activeCount` drops to zero before the skeleton hides.
   * Absorbs brief activeCount=0 dips between guard handoffs / cross-route
   * transitions. Defaults to 500ms (chosen to comfortably exceed typical
   * unmount→mount gaps between adjacent guards).
   */
  hideGraceMs?: number;
}

/**
 * Singleton-skeleton provider. Renders the app's `skeleton` element
 * over its children whenever any descendant calls `useRegisterLoading(true)`,
 * so adjacent loading layers (auth check → workspace check → onboarding, etc.)
 * hand off seamlessly — the skeleton stays mounted across the chain, the
 * animation never resets, and there is no inter-guard flicker.
 *
 * Min-visible (300ms) and delayed-show (100ms) gating is applied once at the
 * provider against the *aggregate* loading state, not per-source.
 */
export function LoadingSkeletonProvider({
  children,
  skeleton,
  minVisibleMs,
  delayShowMs,
  hideGraceMs = 500,
}: LoadingSkeletonProviderProps) {
  const [activeCount, setActiveCount] = useState(0);
  const idsRef = useRef<Set<string>>(new Set());

  const register = useCallback((id: string) => {
    if (idsRef.current.has(id)) return;
    idsRef.current.add(id);
    setActiveCount(idsRef.current.size);
  }, []);

  const unregister = useCallback((id: string) => {
    if (!idsRef.current.delete(id)) return;
    setActiveCount(idsRef.current.size);
  }, []);

  const value = useMemo<LoadingContextValue>(
    () => ({ register, unregister }),
    [register, unregister],
  );
  const showSkeleton = useFlickerGuard(
    activeCount > 0,
    minVisibleMs,
    delayShowMs,
    hideGraceMs,
  );

  // Keep the skeleton mounted at all times and toggle visibility via CSS, so
  // a transient unmount of the visible-state doesn't restart the skeleton's
  // SMIL timeline. When hidden, the skeleton is taken out of layout flow
  // (position: fixed) and made non-interactive so it doesn't block clicks.
  return (
    <LoadingContext.Provider value={value}>
      <div
        aria-hidden={!showSkeleton}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 50,
          visibility: showSkeleton ? 'visible' : 'hidden',
          pointerEvents: showSkeleton ? 'auto' : 'none',
        }}
      >
        {skeleton}
      </div>
      {children}
    </LoadingContext.Provider>
  );
}

/**
 * Registers a loading source with the nearest `LoadingSkeletonProvider`.
 * Returns `true` if a provider is in the tree, `false` otherwise — callers
 * can use that to decide whether to render a local fallback (no provider)
 * or delegate to the singleton (provider in tree).
 *
 * Registration happens in `useLayoutEffect`, so the provider re-renders
 * before paint — no blank-frame gap when a guard mounts mid-load.
 */
export function useRegisterLoading(loading: boolean): boolean {
  const ctx = useContext(LoadingContext);
  const id = useId();
  const hasProvider = ctx !== null;

  useLayoutEffect(() => {
    if (!ctx) return;
    if (loading) {
      ctx.register(id);
      return () => ctx.unregister(id);
    }
  }, [ctx, id, loading]);

  return hasProvider;
}
